<template>
  <div class="page-container">
    <!-- 头部 -->
    <Header />

    <div class="news-detail">
      <section class="about-content">
        <div class="container">
          <div class="article-header" ref="headerSection" :class="{ 'fade-in': isHeaderVisible }">
            <h1>{{ newsDetail.title }}</h1>
            <div class="article-meta">
              <span class="date">{{ formatDate(newsDetail.publish_time) }}</span>
              <span class="views">{{ newsDetail.views }} 阅读</span>
              <span class="author" v-if="newsDetail.author">{{ newsDetail.author }}</span>
            </div>
          </div>

          <div class="article-content" ref="contentSection" :class="{ 'fade-in': isContentVisible }">
       
            <div class="content" v-html="processedContent"></div>
          </div>

          <div class="article-footer">
            <button class="back-btn" @click="goBack">
              <el-icon><ArrowLeft /></el-icon>
              返回列表
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- 底部 -->
    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ArrowLeft } from '@element-plus/icons-vue'
import Header from '@/layout/header/index.vue'
import Footer from '@/layout/footer/index.vue'
import axios from 'axios'
import { getBaseApiUrl } from '@/utils/api-config'

const router = useRouter()
const route = useRoute()
const baseUrl = getBaseApiUrl()
const newsDetail = ref({})
const isHeaderVisible = ref(true)  // 默认显示
const headerSection = ref(null)
const isContentVisible = ref(true)  // 默认显示
const contentSection = ref(null)

// 处理文章内容中的图片路径
const processedContent = computed(() => {
  if (!newsDetail.value.content) return ''

  // 获取媒体服务器地址
  const mediaUrl = (import.meta && import.meta.env && import.meta.env.VITE_APP_MEDIA_URL) || baseUrl

  // 处理图片路径，给相对路径添加服务器地址前缀
  return newsDetail.value.content.replace(
    /<img([^>]*?)src=["']([^"']*?)["']([^>]*?)>/gi,
    (match, before, src, after) => {
      // 如果已经是完整的URL（包含http或https），则不处理
      if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) {
        return match
      }

      // 如果是相对路径，添加媒体服务器地址前缀
      let fullSrc = src
      if (src.startsWith('/')) {
        // 绝对路径，直接拼接
        fullSrc = mediaUrl +'/media'+ src
      } else if (!src.startsWith('./') && !src.startsWith('../')) {
        // 相对路径，添加前缀
        fullSrc = mediaUrl + '/media' + src
      } else {
        // 其他相对路径形式，也添加前缀
        fullSrc = mediaUrl + '/media' + src.replace(/^\.\//, '')
      }

      return `<img${before}src="${fullSrc}"${after}>`
    }
  )
})

// 获取新闻详情
const getNewsDetail = async () => {
  try {
    const id = route.params.id
    const response = await axios.get(`${baseUrl}/api/article/article/${id}/`)
    if (response.data.code === 2000) {
      newsDetail.value = response.data.data.data
    }
  } catch (error) {
    console.error('获取新闻详情失败:', error)
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const goBack = () => {
  router.push({
    name: 'News'
  })
}

onMounted(() => {
  getNewsDetail()

  // 简化处理：页面加载后直接显示所有内容
  // 如果需要动画效果，可以添加延迟显示
  setTimeout(() => {
    isHeaderVisible.value = true
    isContentVisible.value = true
  }, 100)
})
</script>

<style lang="scss" scoped>
.page-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.news-detail {
  flex: 1;
  padding-top: 96px; // header 的高度
  margin-top: 0;

  .about-content {
    padding: 60px 0;
    background: #fff;
    min-height: calc(100vh - 96px - 200px); // 减去 header 和 footer 的高度

    .container {
      max-width: 900px;
      margin: 0 auto;
      padding: 0 20px;

      .article-header {
        text-align: center;
        margin-bottom: 40px;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);

        &.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        h1 {
          font-size: 36px;
          color: #333;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .article-meta {
          color: #999;
          font-size: 14px;

          span {
            margin: 0 10px;
          }
        }
      }

      .article-content {
        opacity: 0;
        transform: translateY(30px);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        .featured-image {
          margin-bottom: 30px;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

          img {
            width: 100%;
            display: block;
          }
        }

        .content {
          font-size: 16px;
          line-height: 1.8;
          color: #333;

          p {
            margin-bottom: 20px;
          }

          img {
            max-width: 100%;
            height: auto;
            margin: 20px 0;
            border-radius: 4px;
          }
        }
      }

      .article-footer {
        margin-top: 60px;
        text-align: center;

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          font-size: 16px;
          color: #2b5dff;
          background: transparent;
          border: 1px solid #2b5dff;
          border-radius: 25px;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            background: rgba(43, 93, 255, 0.05);
            transform: translateY(-2px);
          }

          .el-icon {
            transition: transform 0.3s;
          }

          &:hover .el-icon {
            transform: translateX(-4px);
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .article-header {
      h1 {
        font-size: 28px !important;
      }
    }

    .content {
      font-size: 15px !important;
    }
  }
}
</style> 